import { ProviderUnavailable } from '../TtsProvider';

/**
 * The `/api/tts` request, with its retry rule.
 *
 * Separated from the provider so the retry semantics can be tested without an AudioContext or a
 * Cache — this is the logic most likely to be wrong in a way that only shows up as "the device
 * sometimes says nothing", which is the hardest class of bug to hear about from someone who cannot
 * speak.
 */

export const TTS_FORMAT = 'audio-24khz-48kbitrate-mono-mp3';

/** Matches `api/src/tts.js`. Rejected here rather than paying a round-trip for a 413. */
export const MAX_TEXT_CHARS = 800;

/**
 * Retry a network failure or a 5xx; never a 4xx.
 *
 * A 4xx is our bug or our input — a malformed voice id, text too long — and it will fail again
 * identically. Retrying it just makes the person wait twice as long for the same silence.
 */
export const isRetryable = (status: number | null): boolean => status === null || status >= 500;

/**
 * One retry, no more. A transient `media error 4` occurred in roughly 45 Phase 0 requests, so a
 * communication device should survive a single glitch — but somebody waiting to be heard should not
 * wait through three attempts.
 */
export const MAX_ATTEMPTS = 2;

/**
 * The clock this request runs against (`SPK-17`, C81).
 *
 * There was none. `fetch` has no default timeout, so a radio-wake stall after the iPad has been idle
 * hung the whole chain: no fall-through to Piper, no platform voice, nothing on screen — a tap that
 * appears to do nothing, and a second tap that works because it aborts the first. That is the tester's
 * "nicht auf Anhieb" exactly.
 *
 * Per attempt is double the 2.09 s measured cold start. The total is what stops one retry becoming an
 * eight-second wait for somebody who cannot speak, and the floor is what stops us spending a retry on
 * a budget too small to finish in.
 */
export const TTS_ATTEMPT_BUDGET_MS = 4000;
export const TTS_TOTAL_BUDGET_MS = 6000;
const MIN_MS_WORTH_RETRYING = 1500;

export async function fetchTtsAudio(args: {
  text: string;
  voiceId: string;
  signal: AbortSignal;
  endpoint?: string;
  onRetry?: (reason: string) => void;
  /** Injectable for tests; production reads the same clock everything else does. */
  now?: () => number;
}): Promise<ArrayBuffer> {
  const { text, voiceId, signal, endpoint = '/api/tts', onRetry, now = () => performance.now() } = args;
  let lastError: Error | null = null;
  const startedAtMs = now();
  const remainingMs = (): number => TTS_TOTAL_BUDGET_MS - (now() - startedAtMs);

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
    if (signal.aborted) throw new ProviderUnavailable('azureTts', 'aborted');

    let status: number | null = null;
    /**
     * **A timeout is not a STOP, and this is the line that keeps them apart.**
     *
     * The deadline aborts a controller of our own and never `signal`. If it aborted the caller's
     * signal, `SpeechService.speak()` would see `controller.signal.aborted` and `return null` — the
     * chain would stop dead with Piper and the platform voice untried, and a slow network would read
     * as the person having cancelled. So the timeout raises this provider's own unavailability and
     * the chain moves on, which is what `SPK-01` promises.
     */
    const deadline = new AbortController();
    const budget = Math.min(TTS_ATTEMPT_BUDGET_MS, Math.max(0, remainingMs()));
    const timer = setTimeout(() => deadline.abort(), budget);
    const onCallerAbort = (): void => deadline.abort();
    signal.addEventListener('abort', onCallerAbort, { once: true });

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, voiceId, format: TTS_FORMAT }),
        cache: 'no-store',
        signal: deadline.signal,
      });
      status = response.status;

      if (response.ok) return await response.arrayBuffer();

      // Status only — the response body is deliberately not read into the error.
      //
      // An upstream error body is not ours and could quote what was sent, and this message travels
      // into `SpeechFailed` and from there towards diagnostics. NFR7 forbids utterance text in
      // logs as well as in telemetry, so the body never enters the message in the first place.
      // The Function already logs the upstream status server-side, with no text.
      lastError = new Error(`tts_http_${response.status}`);
    } catch (err) {
      // The caller's STOP wins and is reported as an abort; our own deadline is a timeout, and the
      // difference is what decides whether the chain continues.
      if (signal.aborted) throw new ProviderUnavailable('azureTts', 'aborted');
      lastError = deadline.signal.aborted
        ? new Error('tts_timeout')
        : err instanceof Error
          ? err
          : new Error(String(err));
    } finally {
      clearTimeout(timer);
      signal.removeEventListener('abort', onCallerAbort);
    }

    if (!isRetryable(status)) break;
    // No point starting an attempt there is not time to finish: it would spend the rest of the budget
    // and fail on the clock anyway, having made the person wait for it.
    if (remainingMs() < MIN_MS_WORTH_RETRYING) break;
    if (attempt + 1 < MAX_ATTEMPTS) onRetry?.(lastError?.message ?? 'unknown');
  }

  throw new ProviderUnavailable('azureTts', lastError?.message ?? 'unknown failure');
}
